import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import useTrailTally from '../../hooks/useTrailTally';

const MOUSE_MAP = '/christmas/mouse-map';
const MOUSE_IMAGE = 'https://BelvoirCastle.b-cdn.net/Christmas/MouseTrail/Mouse1.png';

const Band = styled.section`
  background: #101d40;
  color: #fff;
`;

const Panel = styled.section`
  margin: 0 auto clamp(1.75rem, 3vw, 2.25rem);
  border: 1px solid rgba(201, 150, 60, 0.45);
  border-radius: 16px;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 0% 80%, rgba(201, 150, 60, 0.16), transparent 55%),
    #101d40;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.28);
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  align-items: stretch;
  max-width: ${p => (p.$band ? '1180px' : 'none')};
  margin: 0 auto;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled.div`
  padding: ${p => (p.$band ? 'clamp(2rem, 5vw, 3.25rem) clamp(1.5rem, 4vw, 3rem)' : 'clamp(1.35rem, 3vw, 2rem) clamp(1.25rem, 3vw, 2rem)')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
`;

const Kicker = styled.p`
  margin: 0 0 0.7rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #C9963C;
`;

const Title = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: ${p => (p.$band ? 'clamp(2rem, 4.4vw, 3.1rem)' : 'clamp(1.7rem, 3.4vw, 2.35rem)')};
  font-weight: 700;
  line-height: 1.05;
  color: #fff;

  em {
    font-style: italic;
    color: #C9963C;
  }
`;

const Text = styled.p`
  margin: 0 0 1.15rem;
  max-width: 38rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: ${p => (p.$band ? '1rem' : '0.95rem')};
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.78);
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 0 0 1.25rem;
`;

const Stat = styled.div`
  min-width: 7.5rem;
  padding: 0.7rem 0.95rem;
  border: 1px solid rgba(201, 150, 60, 0.4);
  background: rgba(10, 20, 40, 0.45);

  strong {
    display: block;
    font-family: ${p => p.theme.fonts.main};
    font-size: 1.65rem;
    line-height: 1;
    color: #C9963C;
  }

  span {
    display: block;
    margin-top: 0.28rem;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.72);
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
`;

const Primary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.4rem;
  border-radius: 999px;
  background: #C9963C;
  color: #fff;
  text-decoration: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  &:hover {
    background: #A67C2A;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 3px;
  }
`;

const Secondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.3rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  text-decoration: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.06);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 3px;
  }
`;

const PhotoLink = styled(Link)`
  display: block;
  min-height: ${p => (p.$band ? '280px' : '220px')};
  background: #0a1428;

  img {
    display: block;
    width: 100%;
    height: 100%;
    min-height: inherit;
    object-fit: cover;
    object-position: center 78%;
  }

  &:focus-visible {
    outline: 2px solid #C9963C;
    outline-offset: -4px;
  }
`;

const MouseJourneyBanner = ({ band = false }) => {
  const Shell = band ? Band : Panel;
  const tally = useTrailTally();

  return (
    <Shell aria-label="The Great Mouse Journey">
      <Layout $band={band}>
        <Copy $band={band}>
          <Kicker>Starts this weekend · Christmas 2026</Kicker>
          <Title $band={band}>
            The Great Mouse Journey. <em>Find a mouse. Win a family ticket.</em>
          </Title>
          <Text $band={band}>
            Five towns over five weeks, then home to Belvoir. The first clues are already on the map.
            Find a mouse, scan the QR code, and win a free family ticket to the Castle this Christmas.
          </Text>
          {tally && (
            <Stats aria-label={`${tally.found} found in ${tally.town}, ${tally.remaining} still hiding there`}>
              <Stat><strong>{tally.found}</strong><span>Found in {tally.town}</span></Stat>
              <Stat><strong>{tally.remaining}</strong><span>Still hiding in {tally.town}</span></Stat>
            </Stats>
          )}
          <Actions>
            <Primary to={MOUSE_MAP}>Follow the journey</Primary>
            <Secondary to={MOUSE_MAP}>See the first clues</Secondary>
          </Actions>
        </Copy>
        <PhotoLink to={MOUSE_MAP} aria-label="Open the mouse map">
          <img src={MOUSE_IMAGE} alt="A Belvoir mouse in a red coat, waiting in a shop window" />
        </PhotoLink>
      </Layout>
    </Shell>
  );
};

export default MouseJourneyBanner;
